import { MutableRefObject, useCallback, useContext, useEffect, useRef } from 'react'
import SimplePeer from 'simple-peer'
import { isDefinedDeviceId } from '../Logic'
import { IceServerListItem, MuseWSMessage } from '../pages/CollaboratePage/Types'
import { P2pRemoteAudio, RoomContext, RoomContextState, RoomContextType } from '../RoomContext'
import { AudioTrackWithInfo, UserContext, UserContextType } from '../UserContext'
import { arrayDiff, arrayInPlaceRemove, sleep, stripSemverVersion } from '../utils'
import { env } from './EnvironmentVariables'
import { logger, Logger } from './Logger'
import { MusePeer, PeerManifest } from './RoomContextMusePeer'
import sdpTransform from './sdpTransform'
import useRoomActions from './useRoomActions'
import useRoomContextRef from './useRoomContextRef'
import useUserContextRef from './useUserContextRef'
import { wsConnection } from './WSConnectionInstance'
import { WSConnectionSendError } from './WSConnectionManager'
import * as Constants from '../services/Constants'
import { LocalVideoTrack } from 'livekit-client'

/**
 * This component is loaded at <RoomController> when `roomState.expectedNetworkTopology === p2p`.
 * **When the `expectedNetworkTopology` is not p2p, then the component is unloaded.**
 *
 * Connect:
 * - createPeerConnection() is called when `peer.connectionStatus` is 'initial' or
 *   'disconnected_should_initiate_reconnect' (see `peerConnectionStatusEffect()`).
 * - If `peer.callInitiator === 'me'`, then `callPeerWithRetry()` is started.
 *   `callPeerWithRetry()` verifies that the peer is reachable and that this client and the
 *   peer are still valid to connect to the room.
 *     - If this step times out, then TODO: ???
 * - SimplePeer is created and event handlers attached. Connection timeout is implemented
 *   via the `connectionTimeoutRef` timer.
 *
 * Disconnect:
 * If `simplePeerErrorHandler` is called:
 *      -
 *
 *
 */

const _log = logger.createChildLogger({ name: 'MuseP2pPeerManager' })

export default function MuseP2pPeerManager({ peer }: { peer: MusePeer }) {
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType

    const { userStateRef } = useUserContextRef()
    const { roomStateRef } = useRoomContextRef()

    const { leaveRoom, upgradeToSfuRoom: _upgradeToSfuRoom } = useRoomActions()

    const simplePeerRef = useRef<SimplePeer.Instance>()

    // Set to true when component is unloaded.
    const isDestroyedRef = useRef(false)

    // Set to true when upgrade to sfu is triggered via `upgradeToSfuRoom()`.
    const isUpgradingToSfuRef = useRef(false)

    // Set by `simplePeerErrorHandler()`, cleared by `simplePeerCloseHandler()` and `simplePeerErrorHandler()`.
    const connectionTimeoutRef = useRef<NodeJS.Timeout>()

    // Set by `simplePeerErrorHandler()` to be read by `simplePeerCloseHandler()`;
    // cleared by `createPeerConnection()`.
    const lastErrorCodeRef = useRef<string>()

    // Incremented by `simplePeerErrorHandler()`. Is upgraded to sfu when this count is greater than
    // `convert_to_sfu_error_threshold`.
    const simplePeerErrorCount = useRef(0)

    const log = useRef<Logger>(_log)

    const getPeerRef = useCallback(() => {
        return roomStateRef.current.peers[peer.sessionId]
    }, [peer.sessionId, roomStateRef])

    const updatePeerContext = useCallback(
        (next: Partial<MusePeer>) => {
            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: peer.sessionId,
                    peer: next,
                },
            })
        },
        [peer.sessionId, roomDispatch]
    )

    // Manages SimplePeer connection with audio, video, desktop, and midi devices.
    const { updateMediaInfoFromManifest, onSimplePeerTrackReceived } = useP2pMediaController({
        log,
        peer,
        roomStateRef,
        simplePeerRef,
        updatePeerContext,
        getPeerRef,
    })

    const canUpdatePeerContext = useCallback(() => {
        const b1 = isUpgradingToSfuRef.current
        const b2 = isDestroyedRef.current
        const b3 = roomStateRef.current.peers[peer.sessionId]?.sfuConnection != null
        const b4 = roomStateRef.current.expectedNetworkTopology !== 'p2p'

        // if any one of the above is true, then cannot update.
        const cannotUpdate = b1 || b2 || b3 || b4
        const r = !cannotUpdate

        log.current.info('canUpdateConnectionStatus', `returning ${r}.`, {
            isUpgradingToSfu: b1,
            isDestroyedRef: b2,
            peerSfuConnectionDoesNotExist: b3,
            expectedNetworkTopologyIsNotP2p: b4,
        })

        return r
    }, [])

    const simplePeerSignalHandler = useCallback(
        (data: SimplePeer.SignalData) => {
            log.current.verbose(`simplePeerSignalHandler`, `called.`, { data })

            // send this signalling data to the 'peer' with the sessionId 'peerSessionId'.
            // The remote peer should call 'peer.signal(data)' in its handler.

            const offer = {
                type: 'offer-sdp',
                data: {
                    type: data.type, // 'offer' | 'candidate'
                    senderSessionId: userStateRef.current.sessionId,
                    senderUserUrn: userStateRef.current.userUrn,
                    recipient: peer.sessionId,
                    roomUrn: roomStateRef.current.roomUrn,
                    sdp: JSON.stringify(data),
                },
            }

            log.current.verbose(`simplePeerSignalHandler`, `sending "offer-sdp" through signallingWS.`, {
                message: offer,
            })

            if (offer.data.type === 'offer') {
                wsConnection.sendMessage(offer, { isReliable: true, checkAck: true }).then((result) => {
                    log.current.verbose(`simplePeerSignalHandler`, `"offer-sdp" reply result:`, { result })
                    if (result instanceof WSConnectionSendError) {
                        // connection error, even after some retries.
                    } else if (result) {
                        // ack'd.
                    }
                })
            } else {
                wsConnection.sendMessage(offer, { isReliable: false })
            }
        },
        [peer.sessionId, roomStateRef, userStateRef]
    )

    const simplePeerConnectHandler = useCallback(() => {
        if (env.TEST_P2P_CONNECTION_TIMED_OUT) {
            // do not clear timeout
        } else {
            clearTimeout(connectionTimeoutRef.current)
        }

        const peer = getPeerRef()
        log.current.verbose(`simplePeerConnectHandler`, `called.`)
        log.current.checkpoint(`p2p_connected`, ``, {
            peer: {
                sessionId: peer?.sessionId,
                username: peer?.username,
                userUrn: peer?.userUrn,
                appSerialId: peer?.appSerialId,
            },
        })
        updatePeerContext({
            connectionStatus: 'connected',
            p2pConnection: simplePeerRef.current,
        })
    }, [getPeerRef, updatePeerContext])

    const simplePeerTrackReceivedHandler = useCallback((track: MediaStreamTrack, stream: MediaStream) => {
        onSimplePeerTrackReceived(track, stream)
    }, [])

    const simplePeerDataReceivedHandler = useCallback((data_raw: any) => {
        let obj
        try {
            log.current.verbose(
                `simplePeerDataReceivedHandler`,
                `called. parsing data_raw of length=${data_raw?.length}`
            )
            obj = JSON.parse(data_raw)
        } catch (e) {
            return
        }

        log.current.verbose(`simplePeerDataReceivedHandler`, `parsed into JSON successfully.`, { obj })

        const { type, data } = obj
        switch (type) {
            case 'peer-manifest-broadcast':
                let partialManifest = data as PeerManifest
                updateMediaInfoFromManifest(partialManifest)

                roomDispatch({
                    type: 'update_peer_manifest',
                    value: {
                        sessionId: getPeerRef().sessionId,
                        partialManifest: partialManifest,
                    },
                })
                break
        }
    }, [])

    const upgradeToSfuRoom = useCallback(() => {
        isUpgradingToSfuRef.current = true
        _upgradeToSfuRoom()

        if (env.TEST_P2P_CLOSE_AFTER_SFU_UPGRADE) {
            destroyConnectionAndCleanup('closed')
            setTimeout(() => {
                destroyConnectionAndCleanup('closed')
            }, 3000)
        }
    }, [])

    const destroyConnectionAndCleanup = useCallback(
        (connectionStatus: 'closing' | 'closed') => {
            if (simplePeerRef.current) {
                try {
                    simplePeerRef.current.removeAllListeners()
                } catch (e) {}
                try {
                    simplePeerRef.current.destroy()
                } catch (e) {}
                simplePeerRef.current = undefined
            }

            log.current.checkpoint(`p2p_destroy`, '', {
                expectedNetworkTopology: roomStateRef.current.expectedNetworkTopology,
                isUpgradingToSfu: isUpgradingToSfuRef.current,
                isDestroyed: isDestroyedRef.current,
            })

            roomDispatch({
                type: 'update_peer',
                value: {
                    sessionId: getPeerRef().sessionId,
                    peer: {
                        p2pConnection: null,
                        ...(canUpdatePeerContext() ? { connectionStatus } : {}),
                        localP2pAudios: [],
                        localP2pVideo: null,
                        localP2pDesktop: null,
                        localP2pMidiDevices: [],
                    },
                },
                caller: 'destroyConnectionAndCleanup',
                reason: `connection===${connectionStatus}`,
            })
        },
        [getPeerRef, roomDispatch]
    )

    const simplePeerErrorHandler = useCallback((e: Error) => {
        clearTimeout(connectionTimeoutRef.current)

        // @ts-ignore
        if (e?.code === 'ERR_DATA_CHANNEL' && (e?.message || '').includes('User-Initiated Abort')) {
            // not an error.
        } else {
            simplePeerErrorCount.current += 1

            log.current.error(
                `simplePeerErrorHandler`,
                `called; simplePeerErrorCount after increment ` + `is ${simplePeerErrorCount.current}.`,
                {
                    e,
                    simplePeerErrorCount: simplePeerErrorCount.current,
                }
            )
        }

        // @ts-ignore
        const code = e?.code
        lastErrorCodeRef.current = code
    }, [])

    const simplePeerCloseHandler = useCallback(() => {
        clearTimeout(connectionTimeoutRef.current)

        const peer = getPeerRef()
        if (peer == null) {
            return
        }

        const lastErrorCode = lastErrorCodeRef.current
        const lastDisconnectPreambleTime = peer.lastDisconnectPreambleTime

        log.current.checkpoint(`simple_peer_close`, '', {
            lastErrorCode,
            lastDisconnectPreambleTime,
            simplePeerErrorCount: simplePeerErrorCount.current,
        })

        if (lastErrorCode && simplePeerErrorCount.current >= Constants.data.convert_to_sfu_error_threshold) {
            upgradeToSfuRoom()
        }

        destroyConnectionAndCleanup('closing')
    }, [destroyConnectionAndCleanup, getPeerRef])

    /**
     *
     * NOTE: this is an async function that should not be called twice
     * for a given peer while the function is running.
     */
    const callPeer = useCallback(
        async (peer: MusePeer) => {
            try {
                log.current.checkpoint(`p2p_call_peer`)
                const response = await wsConnection.sendMessage(
                    {
                        type: 'dm-peer',
                        data: {
                            type: 'call',

                            senderSessionId: userStateRef.current.sessionId,
                            recipientSessionId: peer.sessionId,

                            senderUserUrn: userStateRef.current.userUrn,
                            senderUsername: userStateRef.current.profile?.username || '',
                            senderDisplayName: userStateRef.current.profile?.displayName,
                            senderIsGuest: userStateRef.current.isGuest,
                            senderAppSerialId: userStateRef.current.appSerialId,
                            senderAppVersion: stripSemverVersion(env.APP_VERSION),
                            clientType: userStateRef.current.clientType,

                            roomUrn: roomStateRef.current.roomUrn,
                        },
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                        tBetweenRetriesMs: 1000,
                        totalTimeoutMs: 3000,
                    }
                )

                log.current.verbose(`callPeer`, `dm-peer/call got response:`, {
                    peer: { sessionId: peer.sessionId, username: peer.username },
                    respose: response,
                })

                if (response instanceof WSConnectionSendError) {
                    log.current.checkpoint(`p2p_call_peer_error:connection_error`, ``, response)
                    return false
                }

                if (response?.error) {
                    log.current.checkpoint(`p2p_call_peer_error:${response.error}`, `cause="${response.cause}"`)
                    if (response.error === 'peer_invalidated') {
                        // likely removed by the host while we were trying to reconnect to this peer.
                        if (canUpdatePeerContext()) {
                            updatePeerContext({
                                connectionStatus: 'closing',
                                lastDisconnectPreambleTime: new Date(),
                                lastErrorCode: null,
                            })
                        }
                    } else if (response.error === 'you_invalidated') {
                        const cause = response.cause
                        if (cause === 'denied_by_host' || cause === 'removed_by_host') {
                            const msg = 'The host removed you from the room.'
                            leaveRoom({ endSession: false, showSnackbarWithMessage: msg })
                        } else if (cause === 'timeout') {
                            // TODO: try rejoining the room since this sessionId got invalidated by being timed out.
                            const msg = 'You timed out while trying to rejoin the room. Please try again.'
                            leaveRoom({ endSession: false, showDialogWithMessage: msg })
                        } else {
                            const msg = `An unexpected error occurred: code="${cause}". Please try re-joining the room.`
                            leaveRoom({ endSession: false, showDialogWithMessage: msg })
                        }
                    }
                    return false
                }

                if (response?.ready) {
                    log.current.checkpoint(`p2p_call_peer_success`)
                    log.current.verbose(`callPeer`, `returning "success"`, {
                        peer: { sessionId: peer.sessionId, username: peer.username },
                        respose: response,
                    })
                    return true
                }

                return false
            } catch (e) {
                log.current.assert(`callPeer`, `sendMessage threw an exception:`, { e })
                return false
            }
        },
        [roomStateRef, updatePeerContext, userStateRef]
    )

    const pingPeerThroughWs = useCallback(
        async (peer: MusePeer) => {
            try {
                const response = await wsConnection.sendMessage(
                    {
                        type: 'dm-peer',
                        data: {
                            type: 'ping',

                            senderSessionId: userStateRef.current.sessionId,
                            recipientSessionId: peer.sessionId,

                            senderUserUrn: userStateRef.current.userUrn,
                            senderUsername: userStateRef.current.profile?.username || '',
                            senderAppSerialId: userStateRef.current.appSerialId,

                            roomUrn: roomStateRef.current.roomUrn,
                        },
                    },
                    {
                        isReliable: true,
                        checkAck: true,
                        tBetweenRetriesMs: 1000,
                        totalTimeoutMs: 3000,
                    }
                )

                log.current.verbose(`pingPeerThroughWs`, `dm-peer/ping got response:`, {
                    peer: { sessionId: peer.sessionId, username: peer.username },
                    respose: response,
                })

                if (response instanceof WSConnectionSendError) {
                    return false
                }

                if (response?.error) {
                    if (response.error === 'peer_invalidated') {
                        // likely removed by the host while we were trying to reconnect to this peer.
                        if (canUpdatePeerContext()) {
                            updatePeerContext({
                                connectionStatus: 'closing',
                                lastDisconnectPreambleTime: new Date(),
                                lastErrorCode: null,
                            })
                        }
                    } else if (response.error === 'you_invalidated') {
                        const cause = response.cause
                        if (cause === 'denied_by_host' || cause === 'removed_by_host') {
                            const msg = 'The host removed you from the room.'
                            leaveRoom({ endSession: false, showSnackbarWithMessage: msg })
                        } else if (cause === 'timeout') {
                            // TODO: try rejoining the room since this sessionId got invalidated by being timed out.
                            const msg = 'You timed out while trying to rejoin the room. Please try again.'
                            leaveRoom({ endSession: false, showDialogWithMessage: msg })
                        } else {
                            const msg = `An unexpected error occurred: code="${cause}". Please try re-joining the room.`
                            leaveRoom({ endSession: false, showDialogWithMessage: msg })
                        }
                    }
                    return false
                }

                if (response?.ready) {
                    log.current.verbose(`pingPeerThroughWs`, `returning "success"`, {
                        peer: { sessionId: peer.sessionId, username: peer.username },
                        respose: response,
                    })
                    return true
                }

                return false
            } catch (e) {
                log.current.assert(`pingPeerThroughWs`, `sendMessage threw an exception:`, { e })
                return false
            }
        },
        [roomStateRef, updatePeerContext, userStateRef]
    )

    /**
     *
     * NOTE: relies on `isDestroyedRef.current` being set correctly upon component destruction.
     */
    const callPeerWithRetry = useCallback(
        async (peerSessionId: string) => {
            let tryNo = 0
            for (;;) {
                let peer = roomStateRef.current.peers[peerSessionId]
                if (!peer || isDestroyedRef.current) {
                    log.current.verbose(`callPeerWithRetry`, `looks like this peer was destroyed. exiting retry loop.`)
                    return false
                }

                // recall: we need to exit the retry loop if component gets destroyed or connection status changes.
                if (
                    peer.connectionStatus === 'initial' ||
                    peer.connectionStatus === 'disconnected_should_initiate_reconnect'
                ) {
                } else {
                    log.current.verbose(`callPeerWithRetry`, `looks like this peer got connected. exiting retry loop.`)
                    return false
                }

                log.current.verbose(`callPeerWithRetry`, `calling callPeer()`, {
                    sessionId: peer.sessionId,
                    username: peer.username,
                    tryNo: tryNo,
                })

                let success = await callPeer(peer)
                if (success) {
                    log.current.verbose(
                        `callPeerWithRetry`,
                        `callPeer() returned success! exiting retry loop ` + `and returning true.`
                    )
                    return success
                }

                await sleep(3000)
                tryNo += 1
            }
        },
        [callPeer, roomStateRef]
    )

    const createPeerConnection = useCallback(
        async (peer: MusePeer) => {
            log.current.verbose(
                `createPeerConnection`,
                `peer.connectionStatus=="${peer.connectionStatus}" ` + `so creating SimplePeer object.`
            )
            // Create SimplePeer object and attempt to connect.

            let iceServers: IceServerListItem[] = Constants.data.ice_servers
            if (iceServers == null || iceServers.length === 0) {
                log.current.assert('noIceServers', `iceServers not provided!`, { iceServers })
                // set a hard-coded default.
                iceServers = [
                    {
                        url: 'stun:musecore.net:80',
                        username: 'museapp',
                        credential: 'museapp',
                    },
                ]
            }

            // Set up the constructor to SimplePeer.
            const simplePeerConfig: SimplePeer.Options = {
                initiator: peer.callInitiator === 'me',
                sdpTransform: (sdp: any) => sdpTransform(sdp),
            }

            if (simplePeerConfig.config == null) {
                simplePeerConfig.config = {}
            }
            // @ts-ignore
            simplePeerConfig.config.iceServers = iceServers

            if (simplePeerRef.current) {
                try {
                    simplePeerRef.current.removeAllListeners()
                } catch (e) {}
                try {
                    simplePeerRef.current.destroy()
                } catch (e) {}
                simplePeerRef.current = undefined
            }

            if (simplePeerConfig.initiator) {
                log.current.verbose(
                    `createPeerConnection`,
                    `checking with the server whether peer with sessionId=${peer.sessionId} exists.`
                )
                // polls until client replies ready
                const success = await callPeerWithRetry(peer.sessionId)
                if (!success) {
                    if (canUpdatePeerContext()) {
                        updatePeerContext({
                            connectionStatus: 'disconnected_should_initiate_reconnect',
                        })
                    }
                    return
                }
            }

            log.current.checkpoint(`p2p_call_simplepeer_constructor`, ``, {
                config: simplePeerConfig,
            })
            log.current.verbose(`createPeerConnection`, `call SimplePeer constructor with config:`, {
                simplePeerConfig,
            })

            lastErrorCodeRef.current = ''

            simplePeerRef.current = new SimplePeer(simplePeerConfig)
            simplePeerRef.current.on('signal', simplePeerSignalHandler)
            simplePeerRef.current.on('connect', simplePeerConnectHandler)
            simplePeerRef.current.on('track', simplePeerTrackReceivedHandler)
            simplePeerRef.current.on('data', simplePeerDataReceivedHandler)
            simplePeerRef.current.on('error', simplePeerErrorHandler)
            simplePeerRef.current.on('close', simplePeerCloseHandler)

            if (env.TEST_P2P_CONNECTION_ERROR) {
                setTimeout(() => {
                    const e = new Error('test_error')
                    // @ts-ignore
                    e.code = 'test_error'
                    simplePeerErrorHandler(e)
                    simplePeerCloseHandler()
                }, 3000)
            }

            // initial connection timeout
            clearTimeout(connectionTimeoutRef.current)
            connectionTimeoutRef.current = setTimeout(() => {
                if (simplePeerRef.current) {
                    simplePeerRef.current.destroy()
                }

                log.current.checkpoint(`p2p_timeout_upgrade_to_sfu`, ``, {
                    p2p_connection_timeout_ms: Constants.data.p2p_connection_timeout_ms,
                })

                upgradeToSfuRoom()
                connectionTimeoutRef.current = undefined
            }, Constants.data.p2p_connection_timeout_ms)

            log.current.verbose(`peerConnectionHandler`, `created SimplePeer and registered event handlers.`)

            if (env.TEST_P2P_CONNECTION_ERROR_NO_ERROR_HANDLER_CALLED) {
                setTimeout(() => {
                    simplePeerCloseHandler()
                }, 500)
            }
        },
        [
            callPeerWithRetry,
            simplePeerCloseHandler,
            simplePeerConnectHandler,
            simplePeerDataReceivedHandler,
            simplePeerErrorHandler,
            simplePeerSignalHandler,
            simplePeerTrackReceivedHandler,
            updatePeerContext,
        ]
    )

    /**
     * NOTE: relies on `isDestroyedRef.current` being set correctly upon component destruction.
     */
    const disconnectedStandbyForCall = useCallback(
        async (peerSessionId: string) => {
            let tryNo = 0
            for (;;) {
                let peer = roomStateRef.current.peers[peerSessionId]
                if (!peer || isDestroyedRef.current) {
                    log.current.verbose(`pingPeerWithRetry`, `looks like this peer was destroyed. exiting retry loop.`)
                    return false
                }

                // recall: we need to exit the retry loop if component gets destroyed or connection status changes.
                if (peer.connectionStatus !== 'disconnected_standby') {
                    log.current.verbose(`pingPeerWithRetry`, `looks like this peer got connected. exiting retry loop.`)
                    return false
                }

                log.current.verbose(`pingPeerWithRetry`, `calling callPeer()`, {
                    sessionId: peer.sessionId,
                    username: peer.username,
                    tryNo: tryNo,
                })

                let success = await pingPeerThroughWs(peer)
                if (success) {
                    log.current.verbose(
                        `pingPeerWithRetry`,
                        `callPeer() returned success! exiting retry loop ` + `and returning true.`
                    )
                    return success
                }

                await sleep(5000)
                tryNo += 1
            }
        },
        [pingPeerThroughWs, roomStateRef]
    )

    useEffect(
        function peerConnectionStatusEffect() {
            log.current.verbose(`peerConnectionStatusEffect`, `effect triggered.`)
            if (
                peer.connectionStatus === 'initial' ||
                peer.connectionStatus === 'disconnected_should_initiate_reconnect'
            ) {
                createPeerConnection(getPeerRef())
            } else if (peer.connectionStatus === 'disconnected_standby') {
                disconnectedStandbyForCall(getPeerRef().sessionId)
            } else {
                log.current.verbose(
                    `peerConnectionStatusEffect`,
                    `peer.connectionStatus=="${peer.connectionStatus}" ` + `so no-op.`
                )
            }
        },
        [peer.connectionStatus]
    )

    useEffect(function wsSignalingServerEffect() {
        function simplePeerForwardSignal(msg: MuseWSMessage) {
            const peer = getPeerRef()
            if (!peer) {
                return
            }

            if (msg.data.senderSessionId === peer.sessionId && msg.data.recipient === userStateRef.current.sessionId) {
                log.current.verbose(`simplePeerForwardSignal`, `received msg=`, { msg })

                // @ts-ignore
                const sdp = msg.data.sdp
                if (simplePeerRef.current) {
                    log.current.verbose(`simplePeerForwardSignal`, `calling simplePeer.signal() with sdp="${sdp}"`)
                    try {
                        simplePeerRef.current.signal(sdp)
                    } catch (e) {}
                } else {
                    log.current.assert(`simplePeerForwardSignal`, `simplePeerRef.current == null!`)
                }
            }
        }

        function onmessage(msg: MuseWSMessage) {
            if (msg.type === 'offer-sdp') {
                simplePeerForwardSignal(msg)
            }
        }

        wsConnection.addEventListener('onmessage', onmessage)

        return () => {
            wsConnection.removeEventListener('onmessage', onmessage)
        }
    }, [])

    useEffect(
        function setLogContext() {
            log.current = logger.createChildLogger({
                name: `MuseP2pPeerManager(sessionId=${peer.sessionId},username=${peer.username})`,
            })
        },
        [peer.sessionId, peer.username]
    )

    useEffect(
        function destructor() {
            isDestroyedRef.current = false
            return () => {
                log.current.verbose(
                    ``,
                    `MuseP2pPeerManager component destructor called for ` +
                        `peer=(sessionId="${getPeerRef().sessionId}", username="${getPeerRef().username}")`
                )

                isDestroyedRef.current = true
                destroyConnectionAndCleanup('closed')
            }
        },
        [destroyConnectionAndCleanup, getPeerRef, roomDispatch]
    )

    log.current.verbose('RENDER', '<MuseP2pPeerManager>')

    return <></>
}

function useP2pMediaController({
    log,
    peer,
    roomStateRef,
    simplePeerRef,
    updatePeerContext,
    getPeerRef,
}: {
    log: MutableRefObject<Logger>
    peer: MusePeer
    roomStateRef: MutableRefObject<RoomContextState>
    simplePeerRef: MutableRefObject<SimplePeer.Instance | undefined>
    updatePeerContext: (next: Partial<MusePeer>) => void
    getPeerRef: () => MusePeer
}) {
    const { dispatch: roomDispatch } = useContext(RoomContext) as RoomContextType
    const { state } = useContext(UserContext) as UserContextType

    const { checkRemotePeersAudioEngineCompatibilityAndShowDialog } = useRoomActions()

    const tracksWaitingManifestRef = useRef<Map<string, { track: MediaStreamTrack; stream: MediaStream }>>(new Map())

    const p2pVideoTrackPublishHelper = useCallback(
        (
            peer: MusePeer,
            stream: LocalVideoTrack | MediaStream | null,
            label: string | undefined,
            localP2pVideoOrDesktop: 'localP2pVideo' | 'localP2pDesktop'
        ) => {
            if (peer.connectionStatus !== 'connected') {
                return
            }

            if (stream?.id !== peer[localP2pVideoOrDesktop]?.stream?.id) {
                if (stream != null) {
                    let track = null
                    let mediaStream = null
                    let isProcessed = false
                    if (stream instanceof LocalVideoTrack) {
                        mediaStream = stream.mediaStream
                        if (stream.getProcessor()) {
                            track = stream.getProcessor()?.processedTrack
                            isProcessed = true
                        } else {
                            track = stream.mediaStreamTrack
                        }
                    } else if (stream instanceof MediaStream) {
                        mediaStream = stream
                        track = stream.getVideoTracks()[0]
                    }
                    if (!(track instanceof MediaStreamTrack)) {
                        log.current.assert(`videoTrackStasis`, `expected stream.getVideoTracks()[0] to be not null.`)
                        return
                    }
                    if (!mediaStream) {
                        log.current.assert(`videoTrackStasis`, `expected mediaStream to be not null.`)
                        return
                    }

                    try {
                        log.current.verbose(`videoTrackStasis`, `call addTrack()`, {
                            streamId: mediaStream.id,
                            displayLabel: label || '(?)',
                        })
                        if (mediaStream) {
                            if (peer[localP2pVideoOrDesktop]) {
                                simplePeerRef.current?.addTrack(track, mediaStream)
                                const oldTrack = peer[localP2pVideoOrDesktop]!.track
                                const oldStream = peer[localP2pVideoOrDesktop]!.stream
                                setTimeout(() => {
                                    if (oldTrack && oldStream) {
                                        simplePeerRef.current?.removeTrack(oldTrack, oldStream)
                                    }
                                }, 500)
                            } else {
                                simplePeerRef.current?.addTrack(track, mediaStream)
                            }
                            updatePeerContext({
                                [localP2pVideoOrDesktop]: { stream: mediaStream, track },
                            })
                        }
                    } catch (e) {
                        log.current.error(`videoTrackStasis`, `an exception occurred while calling addTrack()`, {
                            streamId: mediaStream.id,
                            displayLabel: label || '(?)',
                            e: e,
                        })
                    }
                } else {
                    try {
                        const mediaToRemove = peer[localP2pVideoOrDesktop]
                        if (mediaToRemove == null) {
                            // `streamToRemove` cannot be null becuase of the condition
                            // `(stream?.id !== getSelfPeerRef().localP2pVideoStream?.id)`; note that
                            // stream == null in this if block.
                            log.current.assert(`videoTrackStasis`, `expected streamToRemove to be not null.`)
                            return
                        }

                        simplePeerRef.current?.removeTrack(mediaToRemove.track, mediaToRemove.stream)
                        updatePeerContext({
                            [localP2pVideoOrDesktop]: null,
                        })
                    } catch (e) {
                        log.current.error(`videoTrackStasis`, `an exception occurred while calling removeTrack()`, {
                            e: e,
                        })
                    }
                }
            }
        },
        [updatePeerContext]
    )

    // React to my video stream changing and add/remove/update them in SimplePeer.
    useEffect(
        function p2pVideoTrackPublishManager() {
            if (peer.connectionStatus !== 'connected') {
                return
            }

            const stream =
                state.mediaStreams.video instanceof LocalVideoTrack && state.mediaStreams.video
                    ? state.mediaStreams.video
                    : null

            p2pVideoTrackPublishHelper(getPeerRef(), stream, state.mediaStreams.videoInfo?.label, 'localP2pVideo')
        },
        [
            getPeerRef,
            peer.p2pConnection,
            peer.connectionStatus,
            state.mediaStreams.video,
            state.mediaStreams.videoInfo,
            state.mediaStreams.videoProcessor,
            updatePeerContext,
            p2pVideoTrackPublishHelper,
        ]
    )

    // React to my desktop (presentation) streams changing and add/remove/update them in SimplePeer.
    useEffect(
        function p2pPresentationTrackPublishManager() {
            if (peer.connectionStatus !== 'connected') {
                return
            }

            const stream = state.mediaStreams.desktop instanceof MediaStream ? state.mediaStreams.desktop : null

            p2pVideoTrackPublishHelper(getPeerRef(), stream, '', 'localP2pDesktop')
        },
        [
            getPeerRef,
            peer.p2pConnection,
            peer.connectionStatus,
            state.mediaStreams.desktop,
            state.mediaStreams.desktopInfo,
            updatePeerContext,
            p2pVideoTrackPublishHelper,
        ]
    )

    const _testInitialAudioPublishFailTracker = useRef<{ [key: string]: number }>({})
    const p2pAudioTrackPublisher = useCallback(async (peer: MusePeer, audioInputs: AudioTrackWithInfo[]) => {
        if (peer.connectionStatus !== 'connected') {
            // don't retry b/c p2pAudioTrackPublisherEffect() will call this function whenever connectionStatus changes.
            return { retry: false }
        }

        const atracks: AudioTrackWithInfo[] = audioInputs

        // check if a track in `atracks` exists in `localP2pAudioTracks`
        const { added, removed } = arrayDiff(atracks, getPeerRef().localP2pAudios, (a, b) => a.id === b.track.id)

        log.current.verbose(
            `p2pAudioTrackPublisher`,
            `difference between my atracks and peer model's localP2pAudioTracks:`,
            {
                added: added.map((a) => ({ trackId: a.id, label: a.info.displayLabel })),
                removed: removed.map((a) => ({
                    trackId: a.track.id,
                    streamId: a.stream.id,
                    label: a.track.info.displayLabel,
                })),
            }
        )

        if (added.length + removed.length === 0) {
            log.current.verbose(`p2pAudioTrackPublisher`, `no difference so no-op.`)
            return { retry: false }
        }

        const outAtracksNext = [...getPeerRef().localP2pAudios]

        let failedTracksCt = 0
        for (let addedTrack of added) {
            if (addedTrack.info.audioEngineStreamUuid) {
                log.current.verbose(
                    `p2pAudioTrackPublisher`,
                    `calling audioEngineRoom.sendLocalStream because ` + `audioEngineStreamUuid is set.`,
                    {
                        deviceId: addedTrack.info.deviceId,
                        displayLabel: addedTrack.info.displayLabel,
                    }
                )

                outAtracksNext.push({
                    stream: new MediaStream(),
                    track: addedTrack,
                    filteredTrack: null,
                    gainNode: null,
                })

                if (roomStateRef.current.audioEngineRoom) {
                    roomStateRef.current.audioEngineRoom.sendLocalStream(addedTrack)
                }
            } else {
                const isMono = addedTrack.info.channelCount === 1
                const t = attachGainNode(addedTrack, { isMono })
                const stream = new MediaStream([t.filteredTrack])
                try {
                    log.current.verbose(`p2pAudioTrackPublisher`, `call addTrack()`, {
                        streamId: stream.id,
                        filteredTrackId: t.filteredTrack.id,
                        displayLabel: addedTrack.info.displayLabel,
                    })

                    if (env.TEST_INITIAL_AUDIO_PUBLISH_FAIL_RECOVERY) {
                        if (_testInitialAudioPublishFailTracker.current[addedTrack.id] == null) {
                            _testInitialAudioPublishFailTracker.current[addedTrack.id] = 1
                            throw new Error('TEST_INITIAL_AUDIO_PUBLISH_FAIL_RECOVERY')
                        }
                    }

                    simplePeerRef.current?.addTrack(t.filteredTrack, stream)

                    outAtracksNext.push({
                        stream: stream,
                        track: addedTrack,
                        filteredTrack: t.filteredTrack,
                        gainNode: t.gainNode,
                    })
                } catch (e) {
                    failedTracksCt += 1
                    log.current.error(`p2pAudioTrackPublisher`, `an exception occurred while calling addTrack()`, {
                        streamId: stream.id,
                        filteredTrackId: t.filteredTrack.id,
                        displayLabel: addedTrack.info.displayLabel,
                        e: e,
                    })
                }
            }
        }

        for (let removedTrack of removed) {
            arrayInPlaceRemove(outAtracksNext, (v) => v.track.id === removedTrack.track.id)
            try {
                log.current.verbose(`p2pAudioTrackPublisher`, `call removeTrack()`, {
                    streamId: removedTrack.stream.id,
                    filteredTrackId: removedTrack.filteredTrack?.id,
                    displayLabel: removedTrack.track.info.displayLabel,
                })
                if (removedTrack.filteredTrack) {
                    simplePeerRef.current?.removeTrack(removedTrack.filteredTrack, removedTrack.stream)
                }
            } catch (e) {
                log.current.error(`p2pAudioTrackPublisher`, `an exception occurred while calling removeTrack()`, {
                    streamId: removedTrack.stream.id,
                    filteredTrackId: removedTrack.filteredTrack?.id,
                    displayLabel: removedTrack.track.info.displayLabel,
                    e: e,
                })
            }
        }

        checkRemotePeersAudioEngineCompatibilityAndShowDialog(added, roomStateRef.current.peers)

        updatePeerContext({
            localP2pAudios: outAtracksNext,
        })

        return { retry: failedTracksCt > 0 }
    }, [])

    // React to my audio streams changing and add/remove/update them in SimplePeer.
    let p2pAudioTrackPublisherRetryTimerRef = useRef<NodeJS.Timeout | undefined>(undefined)
    useEffect(
        function p2pAudioTrackPublisherEffect() {
            ;(async () => {
                clearInterval(p2pAudioTrackPublisherRetryTimerRef.current)

                const { retry } = await p2pAudioTrackPublisher(peer, state.mediaStreams.audioInputs)
                if (retry) {
                    _log.info(`p2pAudioTrackPublisherEffect`, `retrying p2pAudioTrackPublisher() in 3s.`)
                    p2pAudioTrackPublisherRetryTimerRef.current = setInterval(async () => {
                        const { retry } = await p2pAudioTrackPublisher(peer, state.mediaStreams.audioInputs)
                        if (!retry) {
                            clearInterval(p2pAudioTrackPublisherRetryTimerRef.current)
                            _log.info(
                                `p2pAudioTrackPublisherEffect`,
                                `p2pAudioTrackPublisher() retry completed successfully.`
                            )
                        } else {
                            _log.info(`p2pAudioTrackPublisherEffect`, `retrying p2pAudioTrackPublisher() in 3s.`)
                        }
                    }, 3000)
                }
            })()
        },
        [state.mediaStreams.audioInputs, peer.connectionStatus]
    )

    // React to my midi devices changing and add/remove/update them in SimplePeer.
    useEffect(
        function midiDevicePublisherEffect() {
            if (peer.connectionStatus !== 'connected') {
                return
            }

            const midiIds: string[] = isDefinedDeviceId(state.mediaSelection.midiInput)
                ? [state.mediaSelection.midiInput]
                : []

            const { added, removed } = arrayDiff(
                midiIds,
                getPeerRef().localP2pMidiDevices,
                (id, b) => id === b.sourceId
            )

            log.current.verbose(
                `midiDeviceStasis`,
                `difference between my midi devices and peer model's localP2pMidiDevices:`,
                {
                    added: added.map((a) => ({ sourceId: a })),
                    removed: removed.map((b) => ({ sourceId: b.sourceId, label: b.label })),
                }
            )

            if (added.length + removed.length === 0) {
                log.current.verbose(`midiDeviceStasis`, `no difference so no-op.`)
                return
            }

            const outMidiNext = [...getPeerRef().localP2pMidiDevices]

            for (let addedSourceId of added) {
                // resolve
                const d = state.mediaSources.midiInputs.find((a) => a.id === addedSourceId)
                if (d) {
                    outMidiNext.push({
                        sourceId: d.id,
                        label: d.name || d.id,
                        isMuted: false,
                    })
                }
            }

            for (let removedDevice of removed) {
                arrayInPlaceRemove(outMidiNext, (v) => v.sourceId === removedDevice.sourceId)
            }

            updatePeerContext({
                localP2pMidiDevices: outMidiNext,
            })
        },
        [
            getPeerRef,
            peer.connectionStatus,
            state.mediaSelection.midiInput,
            state.mediaSelection.midiOutput,
            state.mediaSources.midiInputs,
            updatePeerContext,
        ]
    )

    const updateMediaInfoFromManifest = useCallback(
        (manifest: PeerManifest) => {
            if (manifest.localP2pAudios) {
                const sessionId = getPeerRef().sessionId
                let remoteP2pAudiosNext = [...getPeerRef().remoteP2pAudios]

                manifest.localP2pAudios.forEach((a) => {
                    // attempt to find matching stream.
                    const media = tracksWaitingManifestRef.current.get(a.stream.id)

                    log.current.isVerbose() &&
                        log.current.verbose(
                            `updateMediaInfoFromManifest`,
                            `querying tracksWaitingManifestRef on streamId="${a.stream.id}".`,
                            {
                                found: !!media,
                                tracksWaitingManifestRefKeys: Array.from(tracksWaitingManifestRef.current.keys()),
                            }
                        )

                    if (media) {
                        const rec: P2pRemoteAudio = {
                            fromTopology: 'p2p',
                            stream: media.stream,
                            track: media.track,
                            info: a.info,
                        }

                        const toReplaceIndex = remoteP2pAudiosNext.findIndex((b) => b.stream.id === a.stream.id)

                        if (toReplaceIndex < 0) {
                            remoteP2pAudiosNext.push(rec)
                        } else {
                            remoteP2pAudiosNext[toReplaceIndex] = rec
                        }

                        log.current.isVerbose() &&
                            log.current.verbose(`updateMediaInfoFromManifest`, `updating remoteP2pAudios`, {
                                remoteP2pAudiosNext: remoteP2pAudiosNext.map((a) => ({
                                    streamId: a.stream.id,
                                    trackId: a.track.id,
                                    info: a.info,
                                })),
                            })

                        updatePeerContext({
                            remoteP2pAudios: remoteP2pAudiosNext,
                        })

                        tracksWaitingManifestRef.current.delete(a.stream.id)
                    }
                })
            }
            if (manifest.localP2pVideo) {
                const streamId = manifest.localP2pVideo.stream.id
                const media = tracksWaitingManifestRef.current.get(streamId)
                if (media) {
                    updatePeerContext({
                        remoteP2pVideo: {
                            fromTopology: 'p2p',
                            stream: media.stream,
                            track: media.track,
                        },
                    })
                    tracksWaitingManifestRef.current.delete(streamId)
                }
            }
            if (manifest.localP2pDesktop) {
                const streamId = manifest.localP2pDesktop.stream.id
                const media = tracksWaitingManifestRef.current.get(streamId)
                if (media) {
                    updatePeerContext({
                        remoteP2pDesktop: {
                            fromTopology: 'p2p',
                            stream: media.stream,
                            track: media.track,
                        },
                    })
                    tracksWaitingManifestRef.current.delete(streamId)
                }
            }
        },
        [getPeerRef, updatePeerContext]
    )

    const registerStreamRemovedHandler = useCallback(
        (stream: MediaStream) => {
            stream.onremovetrack = (event: MediaStreamTrackEvent) => {
                const { track: removedTrack } = event
                if (removedTrack.kind === 'video') {
                    log.current.verbose(`registerStreamRemovedHandler`, `detected video stream removed: `, {
                        'removedTrack.id': removedTrack.id,
                        'peer.remoteP2pVideo?.track.id': getPeerRef().remoteP2pVideo?.track?.id || null,
                        'peer.remoteP2pDesktop?.track.id': getPeerRef().remoteP2pDesktop?.track?.id || null,
                    })

                    if (getPeerRef().remoteP2pVideo?.track?.id === removedTrack.id) {
                        roomDispatch({
                            type: 'update_peer',
                            value: {
                                sessionId: getPeerRef().sessionId,
                                peer: { remoteP2pVideo: null, remoteVideo: null },
                            },
                        })
                    }
                    if (getPeerRef().remoteP2pDesktop?.track?.id === removedTrack.id) {
                        roomDispatch({
                            type: 'update_peer',
                            value: {
                                sessionId: getPeerRef().sessionId,
                                peer: { remoteP2pDesktop: null, remoteDesktop: null },
                            },
                        })
                    }
                } else if (removedTrack.kind === 'audio') {
                    const remoteP2pAudiosNext = getPeerRef().remoteP2pAudios.filter(
                        (a) => a.track.id !== removedTrack.id
                    )
                    const remoteAudiosNext = getPeerRef().remoteAudios.filter((a) => a.track.id !== removedTrack.id)
                    roomDispatch({
                        type: 'update_peer',
                        value: {
                            sessionId: getPeerRef().sessionId,
                            peer: { remoteP2pAudios: remoteP2pAudiosNext, remoteAudios: remoteAudiosNext },
                        },
                    })
                }
            }
        },
        [getPeerRef, roomDispatch]
    )

    const deferredManifestCallDebouceTimer = useRef<NodeJS.Timeout | undefined>(undefined)
    const onSimplePeerTrackReceived = (track: MediaStreamTrack, stream: MediaStream) => {
        if (env.TEST_DROPPED_MEDIA_RECEPTION_ON_CONNECT) {
            return
        }

        log.current.verbose(`simplePeerTrackReceivedHandler`, `called.`, {
            trackId: track.id,
            trackKind: track.kind,
            streamId: stream.id,
        })

        tracksWaitingManifestRef.current.set(stream.id, { track, stream })
        registerStreamRemovedHandler(stream)

        // deferred call to `updateMediaInfoFromManifest().` This handles the case where
        // the track arrives faster than the manifest. we implement a debounce so that
        // tracks coming in bursts (e.g. during initial peer connection) do not cause
        // too many calls to `updateMediaInfoFromManifest()` to occur.
        clearTimeout(deferredManifestCallDebouceTimer.current)
        deferredManifestCallDebouceTimer.current = setTimeout(() => {
            updateMediaInfoFromManifest(getPeerRef().manifest)
        }, 100)
    }

    return { updateMediaInfoFromManifest, onSimplePeerTrackReceived }
}

const attachGainNode = (track: MediaStreamTrack, options: { isMono: boolean }) => {
    const endStream = new MediaStream([track])
    const context = new window.AudioContext()
    const microphone = context.createMediaStreamSource(endStream)

    // The regular workflow
    const gainNode = context.createGain()
    gainNode.gain.value = 1

    const destination = context.createMediaStreamDestination()
    const outputStream = destination.stream

    microphone.connect(gainNode)
    gainNode.connect(destination)

    const filteredTrack = outputStream.getAudioTracks()[0]

    return { filteredTrack, gainNode }
}
